import {
  Box,
  FormControl,
  InputAdornment,
  InputLabel,
  ListSubheader,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { useMemo, useState } from "react";
import img_search from "assets/img/search-lg.svg";

export interface ISelectableItem {
  value: string;
  label: string;
}

const containsText = (text, searchText) =>
  text.toLowerCase().indexOf(searchText.toLowerCase()) > -1;

export const SelectWithSearch = ({
  label,
  value,
  options,
  disabled,
  onSelect,
}: {
  label?: string;
  value: ISelectableItem;
  options: ISelectableItem[];
  disabled?: boolean;
  onSelect?: (value: ISelectableItem) => Promise<void>;
}) => {
  const [selectedOption, setSelectedOption] = useState(value);

  const [searchText, setSearchText] = useState("");
  const displayedOptions = useMemo(
    () => options.filter((option) => containsText(option.label, searchText)),
    [searchText]
  );

  return (
    <FormControl disabled={disabled}>
      {!!label && <InputLabel id="search-select-label">Options</InputLabel>}{" "}
      <Select
        sx={{
          borderRadius: "8px",
        }}
        inputProps={{
          sx: {
            color: "var(--gray-900, #101828)",
            borderRadius: "8px",
            padding: "10px 12px",
            border: "1px solid var(--gray-300, #D0D5DD)",
          },
        }}
        // Disables auto focus on MenuItems and allows TextField to be in focus
        MenuProps={{
          autoFocus: false,
          sx: {
            "& .MuiPaper-root": {
              border: "1px solid var(--gray-200, #EAECF0)",
              borderRadius: "8px",
              boxShadow:
                "0px 4px 6px -2px rgba(16, 24, 40, 0.03), 0px 12px 16px -4px rgba(16, 24, 40, 0.08)",
              maxWidth: "280px",
              maxHeight: "320px",
            },
          },
          MenuListProps: {
            sx: {
              padding: "0 6px 6px",
            },
          },
        }}
        labelId="search-select-label"
        id="search-select"
        label={label}
        value={selectedOption.value}
        defaultValue=""
        onChange={async (e) => {
          const val = options.find((option) => option.value === e.target.value);
          if (!val) return;
          if (onSelect) await onSelect(val);
          setSelectedOption(val);
        }}
        onClose={() => setSearchText("")}
        // This prevents rendering empty string in Select's value
        // if search text would exclude currently selected option.
        renderValue={() => selectedOption.label}
      >
        {/* TextField is put into ListSubheader so that it doesn't
        act as a selectable item in the menu
        i.e. we can click the TextField without triggering any selection.*/}
        <ListSubheader
          sx={{
            p: 0,
          }}
        >
          <TextField
            size="small"
            // Autofocus on textfield
            autoFocus
            placeholder="Поиск"
            fullWidth
            InputProps={{
              sx: {
                margin: "6px 0",
              },
              startAdornment: (
                <InputAdornment position="start">
                  <Box
                    component={"img"}
                    width={"20px"}
                    height={"20px"}
                    src={img_search}
                  />
                </InputAdornment>
              ),
            }}
            onChange={(e) => setSearchText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key !== "Escape") {
                // Prevents autoselecting item while typing (default Select behaviour)
                e.stopPropagation();
              }
            }}
          />
        </ListSubheader>

        {displayedOptions.map((option) => (
          <MenuItem
            key={option.value}
            value={option.value}
            aria-label={option.label}
            sx={{
              p: "10px 10px 10px 8px",
              borderRadius: "6px",
            }}
          >
            {option.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};
